import {
  Copy,
  GitBranch,
  LayoutDashboard,
  MousePointer2,
  Sparkles,
  Trash2,
} from "lucide-react";
import type { CSSProperties } from "react";
import type {
  CardColor,
  EditorEdge,
  EditorNode,
  EditorNodeData,
} from "../../core/editor-types";
import type { BoardRecord } from "../../core/model";
import { NODE_RADIUS_MAX, NODE_RADIUS_MIN, clampNodeRadius } from "../../core/node-appearance";

export interface InspectorEdgePatch {
  label?: string;
  color?: string;
  animated?: boolean;
}

export interface InspectorProps {
  selectedNode?: EditorNode | null;
  selectedEdge?: EditorEdge | null;
  selectionCount?: number;
  board?: BoardRecord | null;
  boards?: readonly BoardRecord[];
  onUpdateNode: (nodeId: string, patch: Partial<EditorNodeData>) => void;
  onUpdateEdge: (edgeId: string, patch: InspectorEdgePatch) => void;
  onDuplicateNode: (nodeId: string) => void;
  onMoveBoard?: (boardId: string, parentId: string | null) => void;
  onDuplicateBoard?: (boardId: string) => void;
  onRenameBoard?: (title: string) => void;
  onDeleteSelection: () => void;
  defaultNodeRadius?: number;
  disabled?: boolean;
}

interface ColorOption {
  value: CardColor;
  label: string;
  color: string;
}

export const CARD_COLOR_OPTIONS: readonly ColorOption[] = [
  { value: "paper", label: "纸白", color: "#fffefa" },
  { value: "butter", label: "奶油黄", color: "#f7e6a6" },
  { value: "rose", label: "柔粉", color: "#efc6c3" },
  { value: "mint", label: "薄荷", color: "#bfddca" },
  { value: "sky", label: "天空蓝", color: "#c4d9ef" },
  { value: "lavender", label: "薰衣草", color: "#d8c9ea" },
  { value: "graphite", label: "石墨", color: "#55565a" },
];

const EDGE_COLOR_OPTIONS = [
  { label: "石墨", color: "#737477" },
  { label: "蓝色", color: "#3975de" },
  { label: "绿色", color: "#438b64" },
  { label: "橙色", color: "#c57b32" },
  { label: "红色", color: "#c75a55" },
  { label: "紫色", color: "#8164b5" },
] as const;

function swatchStyle(color: string): CSSProperties {
  return { "--swatch": color } as CSSProperties;
}

export function Inspector({
  selectedNode,
  selectedEdge,
  selectionCount = selectedNode || selectedEdge ? 1 : 0,
  board,
  boards = [],
  onUpdateNode,
  onUpdateEdge,
  onDuplicateNode,
  onMoveBoard,
  onDuplicateBoard,
  onRenameBoard,
  onDeleteSelection,
  defaultNodeRadius = 0,
  disabled = false,
}: InspectorProps) {
  if (!selectedNode && !selectedEdge) {
    const descendants = new Set<string>();
    if (board) {
      const queue = [board.id];
      while (queue.length) {
        const parentId = queue.shift()!;
        for (const candidate of boards) {
          if (candidate.parentId === parentId && !descendants.has(candidate.id)) {
            descendants.add(candidate.id);
            queue.push(candidate.id);
          }
        }
      }
    }
    const parentOptions = boards.filter((candidate) => candidate.id !== board?.id && !descendants.has(candidate.id));
    return (
      <aside id="canvas-inspector" className="inspector" aria-label="画板检查器">
        <div className="inspector__header">
          <strong>画板属性</strong>
          <span className="inspector__selection">未选择组件</span>
        </div>
        {board ? (
          <>
            <section className="inspector-section board-inspector-summary">
              <LayoutDashboard size={22} aria-hidden="true" />
              <div><strong>{board.title}</strong><code title={board.id}>{board.id}</code></div>
            </section>
            <section className="inspector-section">
              <label className="inspector-label" htmlFor="inspector-board-title">画板名称</label>
              <input
                id="inspector-board-title"
                className="inspector-input"
                value={board.title}
                onChange={(event) => onRenameBoard?.(event.currentTarget.value)}
                maxLength={240}
                disabled={disabled || !onRenameBoard}
              />
            </section>
            <section className="inspector-section">
              <label className="inspector-label" htmlFor="inspector-board-parent">父级画板</label>
              <select
                id="inspector-board-parent"
                className="inspector-input"
                value={board.parentId ?? ""}
                onChange={(event) => onMoveBoard?.(board.id, event.currentTarget.value || null)}
                disabled={disabled || !onMoveBoard}
              >
                <option value="">项目顶层</option>
                {parentOptions.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>{candidate.title}</option>
                ))}
              </select>
              <p className="inspector-help">也可以直接把左侧画板拖到另一个画板上；拖到“画板”标题可移回顶层。</p>
            </section>
            <section className="inspector-section">
              <span className="inspector-label">结构操作</span>
              <button
                type="button"
                className="inspector-action inspector-action--wide"
                onClick={() => onDuplicateBoard?.(board.id)}
                disabled={disabled || !onDuplicateBoard}
              >
                <Copy size={14} aria-hidden="true" />
                复制画板及全部子画板
              </button>
            </section>
          </>
        ) : (
          <div className="inspector-empty">
            <MousePointer2 size={28} aria-hidden="true" />
            <strong>选择画布内容</strong>
            <p>选择组件或连接线后，可在这里调整它的属性。</p>
          </div>
        )}
      </aside>
    );
  }

  if (selectedNode) {
    const activeColor = selectedNode.data.color ?? "paper";
    const hasRadiusOverride = typeof selectedNode.data.borderRadius === "number";
    const activeRadius = hasRadiusOverride ? selectedNode.data.borderRadius! : defaultNodeRadius;
    const noteAutoHeight = selectedNode.data.autoHeight !== false;

    return (
      <aside id="canvas-inspector" className="inspector" aria-label="组件检查器">
        <div className="inspector__header">
          <strong>组件属性</strong>
          <span className="inspector__selection">
            {selectionCount > 1 ? `已选择 ${selectionCount} 项` : selectedNode.data.kind}
          </span>
        </div>

        <section className="inspector-section">
          <label className="inspector-label" htmlFor="inspector-node-title">
            标题
          </label>
          <input
            id="inspector-node-title"
            className="inspector-input"
            value={selectedNode.data.title}
            onChange={(event) =>
              onUpdateNode(selectedNode.id, { title: event.currentTarget.value })
            }
            maxLength={240}
            disabled={disabled}
          />
        </section>

        <section className="inspector-section">
          <div className="inspector-label-row">
            <label className="inspector-label" htmlFor="inspector-node-radius">圆角</label>
            <output htmlFor="inspector-node-radius">
              {hasRadiusOverride ? `${activeRadius} px` : `默认 ${defaultNodeRadius} px`}
            </output>
          </div>
          <div className="inspector-range-line">
            <input
              id="inspector-node-radius"
              className="settings-range"
              type="range"
              min={NODE_RADIUS_MIN}
              max={NODE_RADIUS_MAX}
              step={1}
              value={activeRadius}
              onChange={(event) => onUpdateNode(selectedNode.id, {
                borderRadius: clampNodeRadius(Number(event.currentTarget.value)),
              })}
              disabled={disabled}
            />
            <button
              type="button"
              className="inspector-radius-reset"
              onClick={() => onUpdateNode(selectedNode.id, { borderRadius: undefined })}
              disabled={disabled || !hasRadiusOverride}
              title="跟随设置中的组件默认圆角"
            >
              跟随默认
            </button>
          </div>
          <p className="inspector-help">单独设置只保存在当前组件；跟随默认时不写入固定值。</p>
        </section>

        <section className="inspector-section">
          <span className="inspector-label">卡片颜色</span>
          <div className="color-grid" role="group" aria-label="卡片颜色">
            {CARD_COLOR_OPTIONS.map((option) => (
              <button
                type="button"
                key={option.value}
                className={`color-swatch${activeColor === option.value ? " is-active" : ""}`}
                style={swatchStyle(option.color)}
                onClick={() =>
                  onUpdateNode(selectedNode.id, { color: option.value })
                }
                disabled={disabled}
                aria-label={option.label}
                aria-pressed={activeColor === option.value}
                title={option.label}
              />
            ))}
          </div>
        </section>

        {selectedNode.data.kind === "note" ? (
          <section className="inspector-section">
            <div className="inspector-toggle-row">
              <label className="inspector-label" htmlFor="inspector-note-auto-height">
                自适应内容高度
              </label>
              <button
                id="inspector-note-auto-height"
                type="button"
                className="settings-switch"
                role="switch"
                aria-checked={noteAutoHeight}
                onClick={() => onUpdateNode(selectedNode.id, { autoHeight: !noteAutoHeight })}
                disabled={disabled}
              >
                <span aria-hidden="true" />
                <span className="visually-hidden">{noteAutoHeight ? "已启用" : "已禁用"}</span>
              </button>
            </div>
            <p className="inspector-help">编辑时实时跟随 Markdown 内容调整高度；关闭后可自由固定便签高度。</p>
          </section>
        ) : null}

        <section className="inspector-section">
          <span className="inspector-label">操作</span>
          <div className="inspector-actions">
            <button
              type="button"
              className="inspector-action"
              onClick={() => onDuplicateNode(selectedNode.id)}
              disabled={disabled || selectionCount > 1}
            >
              <Copy size={14} aria-hidden="true" />
              复制
            </button>
            <button
              type="button"
              className="inspector-action is-danger"
              onClick={onDeleteSelection}
              disabled={disabled}
            >
              <Trash2 size={14} aria-hidden="true" />
              删除
            </button>
          </div>
        </section>
      </aside>
    );
  }

  const edge = selectedEdge as EditorEdge;
  const edgeLabel = edge.data?.label ?? "";
  const edgeColor = edge.data?.color ?? EDGE_COLOR_OPTIONS[0].color;

  return (
    <aside id="canvas-inspector" className="inspector" aria-label="连接线检查器">
      <div className="inspector__header">
        <strong>连接线属性</strong>
        <span className="inspector__selection">
          {selectionCount > 1 ? `已选择 ${selectionCount} 项` : "连接线"}
        </span>
      </div>

      <section className="inspector-section">
        <label className="inspector-label" htmlFor="inspector-edge-label">
          标签
        </label>
        <input
          id="inspector-edge-label"
          className="inspector-input"
          value={edgeLabel}
          onChange={(event) =>
            onUpdateEdge(edge.id, { label: event.currentTarget.value })
          }
          placeholder="可选的连接说明"
          maxLength={160}
          disabled={disabled}
        />
      </section>

      <section className="inspector-section">
        <span className="inspector-label">线条颜色</span>
        <div className="color-grid" role="group" aria-label="线条颜色">
          {EDGE_COLOR_OPTIONS.map((option) => (
            <button
              type="button"
              key={option.color}
              className={`color-swatch${edgeColor === option.color ? " is-active" : ""}`}
              style={swatchStyle(option.color)}
              onClick={() => onUpdateEdge(edge.id, { color: option.color })}
              disabled={disabled}
              aria-label={option.label}
              aria-pressed={edgeColor === option.color}
              title={option.label}
            />
          ))}
        </div>
      </section>

      <section className="inspector-section">
        <span className="inspector-label">操作</span>
        <div className="inspector-actions">
          <button
            type="button"
            className="inspector-action"
            onClick={() => onUpdateEdge(edge.id, { animated: !edge.animated })}
            disabled={disabled || selectionCount > 1}
            aria-pressed={Boolean(edge.animated)}
          >
            {edge.animated ? (
              <Sparkles size={14} aria-hidden="true" />
            ) : (
              <GitBranch size={14} aria-hidden="true" />
            )}
            {edge.animated ? "动态" : "静态"}
          </button>
          <button
            type="button"
            className="inspector-action is-danger"
            onClick={onDeleteSelection}
            disabled={disabled}
          >
            <Trash2 size={14} aria-hidden="true" />
            删除
          </button>
        </div>
      </section>
    </aside>
  );
}
